import { Popover, PopoverContent, PopoverTrigger } from '@commenda/kakashi';
import { cn } from '@commenda/utils';
import { useMemo, useState, type ReactNode } from 'react';

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandList,
} from '../ui/command';
import {
  ComposerPickerMenuHelper,
  type IComposerPickerOption,
} from './composer-picker-menu-helper';
import { ComposerPickerMenuOption } from './ComposerPickerMenuOption';

interface ComposerPickerMenuProps {
  /** The pill that opens the menu, rendered as the trigger itself rather than inside a form control. */
  trigger: ReactNode;
  options: readonly IComposerPickerOption[];
  value: string;
  onValueChange: (value: string) => void;
  /** Given, a search field is pinned above the list. Absent renders no search at all. */
  searchPlaceholder?: string;
  emptyText: string;
  /** The design fixes each menu's width independently of its pill, so it is never derived. */
  widthClassName: string;
}

/** The dropdown every composer picker uses (Figma A-M, nodes 849:25468 and 840:24975: one
 * component, two instances, with search and the group header toggled per use).
 *
 * Not built on `SingleCombobox`: that component owns its trigger, filling a fixed `h-10 w-full`
 * bordered button, and pins its popover width to the trigger with an inline style. */
export const ComposerPickerMenu = ({
  trigger,
  options,
  value,
  onValueChange,
  searchPlaceholder,
  emptyText,
  widthClassName,
}: ComposerPickerMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Filtered here with `shouldFilter={false}` below, the way SingleCombobox does it.
  const visibleOptions = useMemo(
    () =>
      options.filter((option) =>
        ComposerPickerMenuHelper.matchesSearch(option, search)
      ),
    [options, search]
  );
  const ungroupedOptions = useMemo(
    () => visibleOptions.filter((option) => !option.group),
    [visibleOptions]
  );
  const groupLabels = useMemo(
    () => ComposerPickerMenuHelper.groupLabels(visibleOptions),
    [visibleOptions]
  );

  const renderOption = (option: IComposerPickerOption) => (
    <ComposerPickerMenuOption
      key={option.value}
      option={option}
      isSelected={option.value === value}
      onSelect={(nextValue) => {
        onValueChange(nextValue);
        setIsOpen(false);
      }}
    />
  );

  return (
    <Popover
      open={isOpen}
      onOpenChange={(nextIsOpen) => {
        setIsOpen(nextIsOpen);
        if (!nextIsOpen) setSearch('');
      }}
    >
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align="start"
        // Opens upward: every picker sits on the composer, near the bottom of the screen.
        side="top"
        sideOffset={6}
        className={cn(
          'overflow-hidden rounded-xl border-1/2 border-border-primary p-none shadow-[0px_2px_8px_rgba(0,0,0,0.04)]',
          widthClassName
        )}
      >
        {/* The shared Command's chat-scoped tokens do not resolve here, and its input keeps the
            border @tailwindcss/forms puts on every field, so both are overridden. */}
        <Command
          shouldFilter={false}
          className="bg-bg-primary text-text-primary [&_[data-cmdk-input-wrapper]]:h-9 [&_[data-cmdk-input-wrapper]]:border-b-border-primary [&_[data-cmdk-input-wrapper]]:px-lg [&_[data-cmdk-input-wrapper]>svg]:hidden"
        >
          {searchPlaceholder !== undefined && (
            <CommandInput
              placeholder={searchPlaceholder}
              value={search}
              onValueChange={setSearch}
              className="h-auto rounded-none border-0 p-none txt-small-regular text-text-primary placeholder:text-text-placeholder focus:border-0 focus:ring-0"
            />
          )}
          {/* 337px is the design's list height (Figma A-M 849:25471). */}
          <CommandList className="max-h-[337px] py-sm">
            {visibleOptions.length === 0 && (
              <CommandEmpty>{emptyText}</CommandEmpty>
            )}
            {ungroupedOptions.map(renderOption)}
            {groupLabels.map((groupLabel) => (
              <CommandGroup
                key={groupLabel}
                heading={groupLabel}
                className="[&_[cmdk-group-heading]]:!px-lg [&_[cmdk-group-heading]]:!py-md [&_[cmdk-group-heading]]:!txt-extra-small-medium [&_[cmdk-group-heading]]:!text-text-tertiary"
              >
                {visibleOptions
                  .filter((option) => option.group === groupLabel)
                  .map(renderOption)}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
